import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import {
  LEGAL_DOCUMENT_TYPE_LABELS,
  LEGAL_PLATFORM_LABELS,
  LegalAcceptance,
  LegalDocument,
  LegalPendingUser
} from '../../models/legal-document';
import { LegalDocumentService } from '../../../services/legal-document.service';

const PAGE_SIZE = 25;

type PendingAction = 'publish' | 'delete';

/**
 * One version: its rendered text, and — once published — who accepted it and when, and who has not
 * yet. A draft is edited, deleted or published from here, with the rendered text in view.
 */
@Component({
  selector: 'app-legal-document-detail',
  templateUrl: './legal-document-detail.component.html',
  styleUrls: ['./legal-document-detail.component.scss']
})
export class LegalDocumentDetailComponent implements OnInit, OnDestroy {

  readonly typeLabels = LEGAL_DOCUMENT_TYPE_LABELS;
  readonly platformLabels = LEGAL_PLATFORM_LABELS;
  readonly pageSize = PAGE_SIZE;

  documentId!: string;
  document: LegalDocument | null = null;
  loading = true;
  error: string | null = null;

  formOpen = false;
  pendingAction: PendingAction | null = null;
  actionBusy = false;
  actionError: string | null = null;
  exporting = false;
  copying = false;

  tabIndex = 0;

  acceptances: LegalAcceptance[] = [];
  acceptancesPage = 0;
  acceptancesTotal = 0;
  acceptancesLoading = false;
  acceptancesError: string | null = null;

  pending: LegalPendingUser[] = [];
  pendingPage = 0;
  pendingTotal = 0;
  pendingLoading = false;
  pendingError: string | null = null;

  private readonly destroy$ = new Subject<void>();

  constructor(private route: ActivatedRoute, private router: Router, private legalService: LegalDocumentService) {}

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.documentId = params.get('id') ?? '';
      this.document = null;
      this.loading = true;
      this.tabIndex = 0;
      this.loadDocument();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadDocument(): void {
    this.error = null;
    this.legalService.get(this.documentId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (document) => {
        this.document = document;
        this.loading = false;
        if (document.status === 'PUBLISHED') {
          this.acceptancesPage = 0;
          this.pendingPage = 0;
          this.loadAcceptances();
          if (document.current) {
            this.loadPending();
          }
        }
      },
      error: (err) => {
        this.error = err?.status === 404 ? 'This document does not exist.' : 'Could not load the document.';
        this.loading = false;
      }
    });
  }

  loadAcceptances(): void {
    this.acceptancesLoading = true;
    this.acceptancesError = null;
    this.legalService.acceptances(this.documentId, this.acceptancesPage, PAGE_SIZE)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (page) => {
          this.acceptances = page.data ?? [];
          this.acceptancesTotal = page.total;
          this.acceptancesLoading = false;
        },
        error: () => {
          this.acceptancesError = 'Could not load the acceptances.';
          this.acceptancesLoading = false;
        }
      });
  }

  loadPending(): void {
    this.pendingLoading = true;
    this.pendingError = null;
    this.legalService.pending(this.documentId, this.pendingPage, PAGE_SIZE)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (page) => {
          this.pending = page.data ?? [];
          this.pendingTotal = page.total;
          this.pendingLoading = false;
        },
        error: () => {
          this.pendingError = 'Could not load who has not accepted yet.';
          this.pendingLoading = false;
        }
      });
  }

  goToAcceptancesPage(page: number): void {
    this.acceptancesPage = page;
    this.loadAcceptances();
  }

  goToPendingPage(page: number): void {
    this.pendingPage = page;
    this.loadPending();
  }

  totalPages(total: number): number {
    return Math.max(1, Math.ceil(total / PAGE_SIZE));
  }

  get statusLabel(): string {
    const d = this.document;
    if (!d || d.status === 'DRAFT') {
      return 'Draft';
    }
    return d.current ? 'Current' : 'Superseded';
  }

  get statusClass(): string {
    const d = this.document;
    if (!d || d.status === 'DRAFT') {
      return 'catalog-status status-draft';
    }
    return d.current ? 'catalog-status status-published' : 'catalog-status status-archived';
  }

  // ── Actions ────────────────────────────────────────────────────────────────

  onSaved(document: LegalDocument): void {
    this.document = document;
    this.formOpen = false;
  }

  /** A change to a published version is a new draft starting from its text. */
  newVersion(): void {
    if (!this.document) {
      return;
    }
    this.copying = true;
    this.actionError = null;
    this.legalService.copy(this.document.id).subscribe({
      next: (copy) => {
        this.copying = false;
        this.router.navigate(['/legal-documents', copy.id]).then(() => this.formOpen = true);
      },
      error: (err) => {
        this.copying = false;
        this.actionError = err?.error?.exceptionMessage || err?.error?.message || 'Could not start a new version.';
      }
    });
  }

  get confirmTitle(): string {
    return this.pendingAction === 'publish' ? 'Publish this version?' : 'Delete this draft?';
  }

  get confirmMessage(): string {
    if (this.pendingAction === 'delete') {
      return 'The draft is thrown away. Published versions are not affected.';
    }
    const app = this.document ? this.platformLabels[this.document.platform] : '';
    const type = this.document ? this.typeLabels[this.document.type] : '';
    return `It becomes the current ${app} ${type}. Every ${app} user will have to accept it before `
      + 'they can continue working there. A published version cannot be edited or deleted.';
  }

  askAction(action: PendingAction): void {
    this.actionError = null;
    this.pendingAction = action;
  }

  confirmAction(): void {
    if (!this.document || !this.pendingAction) {
      return;
    }
    const action = this.pendingAction;
    this.actionBusy = true;
    if (action === 'delete') {
      this.legalService.delete(this.document.id).subscribe({
        next: () => {
          this.actionBusy = false;
          this.pendingAction = null;
          this.router.navigate(['/legal-documents'], { queryParams: { platform: this.document?.platform } });
        },
        error: (err) => this.actionFailed(err, 'The draft could not be deleted.')
      });
      return;
    }
    this.legalService.publish(this.document.id).subscribe({
      next: () => {
        this.actionBusy = false;
        this.pendingAction = null;
        this.loading = true;
        this.loadDocument();
      },
      error: (err) => this.actionFailed(err, 'The version could not be published.')
    });
  }

  exportCsv(): void {
    if (!this.document) {
      return;
    }
    this.exporting = true;
    const document = this.document;
    this.legalService.exportAcceptances(document.id).subscribe({
      next: (blob) => {
        this.exporting = false;
        const url = URL.createObjectURL(blob);
        const link = window.document.createElement('a');
        link.href = url;
        link.download = `prihvatanja-${document.platform.toLowerCase()}-${document.type.toLowerCase()}-v${document.version}.csv`;
        link.click();
        URL.revokeObjectURL(url);
      },
      error: () => {
        this.exporting = false;
        this.actionError = 'The export could not be downloaded.';
      }
    });
  }

  private actionFailed(err: any, fallback: string): void {
    this.actionBusy = false;
    this.pendingAction = null;
    this.actionError = err?.error?.exceptionMessage || err?.error?.message || fallback;
  }
}
